import { computed, inject, Injectable, signal } from '@angular/core';
import { AngularGridInstance } from 'angular-slickgrid';
import { IGridLayout, ColumnFilter, GridSnapshot } from '../interface/IGridSnapshot';
import { LayoutDto } from './proxy/dtos/layout';
import { IConfirmationPopupParams } from '../../interfaces/IConfirmationPopupParams';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { LayoutService } from './proxy/controllers';
import { finalize, map, Observable, shareReplay, tap } from 'rxjs';
import { PreAccessFilterValue } from '../../pre-access-filter/models/pre-access-filter.models';
import { PreAccessFilterMergeService } from '../../pre-access-filter/services/pre-access-filter-merge.service';
import { GridStateApplierService } from '../../services/grid-state-applier.service';
import { FieldDefinition } from '../../fields/field-definition';

@Injectable({
  providedIn: 'root',
})
export class GridLayout {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly layoutService = inject(LayoutService);
  private readonly toaster = inject(ToasterService);
  private readonly preAccessMergeService = inject(PreAccessFilterMergeService);
  private readonly gridStateApplier = inject(GridStateApplierService);

  public grid: AngularGridInstance;
  private entity: string;
  private localizationString: string;
  private permissionName: string;

  readonly layouts = signal<IGridLayout[]>([]);
  readonly selectedLayout = signal<IGridLayout | null>(null);
  readonly favoriteLayout = signal<IGridLayout | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly error = signal<any | null>(null);

  readonly preAccessFiltersApplied = signal<PreAccessFilterValue[]>([]);
  private suppressInitialLayoutQuery = false;
  private defaultHiddenColumns = new Set<string>();
  private enforcedPrefilterColumnIds = new Set<string>();

  private rolesCache$: Observable<string[]> | null = null;
  private rolesSignal = signal<string[]>([]);
  readonly availableRoles = this.rolesSignal.asReadonly();

  readonly hasLayouts = computed(() => this.layouts().length > 0);
  readonly personalLayouts = computed(() => this.layouts().filter(l => l.isPersonal));
  readonly sharedLayouts = computed(() => this.layouts().filter(l => !l.isPersonal));

  get layout() {
    return this.selectedLayout();
  }

  set layout(layout: IGridLayout) {
    this.selectedLayout.set(layout);
  }

  get entityName() {
    return this.entity;
  }

  get localizationKey() {
    return this.localizationString;
  }

  get layoutList() {
    return this.layouts();
  }

  set layoutList(values: IGridLayout[]) {
    this.layouts.set([...values]);
  }

  initialize(
    instance: AngularGridInstance,
    entityName: string,
    localizationKey: string,
    permissionName: string,
    fields: FieldDefinition[] = []
  ): void {
    this.grid = instance;
    this.entity = entityName;
    this.localizationString = localizationKey;
    this.permissionName = permissionName;
    this.defaultHiddenColumns = this.buildDefaultHiddenColumns(fields);
    this.enforcedPrefilterColumnIds = new Set(
      fields
        .filter(field => field.fieldOptions?.prefilter?.enforceOnQueries === true)
        .map(field => field.key)
    );

    this.preAccessFiltersApplied.set([]);

    this.loadInitialData();
  }

  setSuppressInitialLayoutQuery(value: boolean): void {
    this.suppressInitialLayoutQuery = value;
  }

  private loadInitialData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.layoutService
      .getList(this.entityName, { skipHandleError: true })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: layouts => {
          this.processLayouts(layouts);
        },
        error: error => {
          this.error.set(error);
          this.toaster.error(error);
        },
      });
  }

  private getRoles(): Observable<string[]> {
    if (!this.rolesCache$) {
      this.rolesCache$ = this.layoutService.getRolesByPermissionName(this.permissionName).pipe(
        map(response => response.map(role => role.providerKey)),
        shareReplay(1)
      );
    }
    return this.rolesCache$;
  }

  loadRoles(): void {
    if (this.rolesSignal().length === 0) {
      this.getRoles().subscribe(roles => this.rolesSignal.set(roles));
    }
  }

  private processLayouts(layouts: any[]): void {
    const mappedLayouts = this.layoutMapper(layouts);
    const favorite = mappedLayouts.find(l => l.isFavorite) || null;
    const basic = mappedLayouts.find(l => l.isBasic);

    this.favoriteLayout.set(favorite);

    if (!basic) {
      this.processBasicLayout(mappedLayouts);
    } else {
      const layoutToApply = favorite || basic;
      this.layouts.set(mappedLayouts);
      this.selectedLayout.set(layoutToApply);
      this.applySnapshot(layoutToApply.snapshot, 'preaccess', {
        triggerQuery: !this.suppressInitialLayoutQuery,
      });
      this.suppressInitialLayoutQuery = false;
    }
  }

  private processBasicLayout(list: IGridLayout[]): void {
    const generatedBasicLayout = this.createBasicLayout();
    const favorite = this.favoriteLayout();

    if (!favorite) {
      this.selectedLayout.set(generatedBasicLayout);
      this.favoriteLayout.set(generatedBasicLayout);
      this.layouts.set([generatedBasicLayout, ...list]);
      this.gridStateApplier.applySnapshot(this.grid, generatedBasicLayout.snapshot, {
        triggerQuery: !this.suppressInitialLayoutQuery,
      });
      this.suppressInitialLayoutQuery = false;
    } else {
      const filteredList = list.filter(layout => layout.id !== favorite.id);
      this.selectedLayout.set(favorite);
      this.layouts.set([favorite, generatedBasicLayout, ...filteredList]);
      this.applySnapshot(favorite.snapshot, 'preaccess', {
        triggerQuery: !this.suppressInitialLayoutQuery,
      });
      this.suppressInitialLayoutQuery = false;
    }
  }

  layoutMapper(layouts: LayoutDto[]): IGridLayout[] {
    const mappedLayouts = layouts.map(layout => {
      return {
        ...layout,
        isPersonal: layout.isBasic ? false : layout?.sharedRoles.length === 0,
        snapshot: JSON.parse(layout.definition),
      } as IGridLayout;
    });
    return mappedLayouts;
  }
  
  createBasicLayout(): IGridLayout {
    const snapshot = this.applyDefaultHiddenColumns(this.captureSnapshot());

    return {
      id: -1,
      name: 'TableBasicLayout',
      isBasic: true,
      isFavorite: !this.favoriteLayout(),
      isPersonal: false,
      sharedRoles: [],
      snapshot
    };
  }

  private buildDefaultHiddenColumns(fields: FieldDefinition[]): Set<string> {
    return new Set(
      fields.flatMap(field => {
        const hiddenColumns: string[] = [];

        if (field.fieldOptions?.hiddenByDefault === true) {
          hiddenColumns.push(field.key);
        }

        hiddenColumns.push(
          ...(field.additionalGridColumns ?? [])
            .filter(column => column.hiddenByDefault === true)
            .map(column => column.key)
        );

        return hiddenColumns;
      })
    );
  }

  private applyDefaultHiddenColumns(snapshot: GridSnapshot): GridSnapshot {
    const existingColumnIds = new Set(snapshot.columnVisibility.map(col => col.columnId));
    const columnVisibility = snapshot.columnVisibility.map(col => ({
      ...col,
      visible: this.defaultHiddenColumns.has(col.columnId) ? false : col.visible,
    }));

    this.defaultHiddenColumns.forEach(columnId => {
      if (!existingColumnIds.has(columnId)) {
        columnVisibility.push({ columnId, visible: false });
      }
    });

    return {
      ...snapshot,
      columnVisibility,
    };
  }

  confirmationPopup(options: IConfirmationPopupParams) {
    return this.confirmationService.show(
      options.message,
      options.title,
      options.severity,
      options.options
    );
  }

  captureSnapshot(isEditingBasicLayout?: boolean): GridSnapshot {
    const includeFilters = !(this.selectedLayout()?.isBasic && isEditingBasicLayout);
    return this.gridStateApplier.captureSnapshot(this.grid, { includeFilters });
  }

  applySnapshot(
    snapshot: GridSnapshot,
    priority: 'preaccess' | 'layout' = 'preaccess',
    options?: { triggerQuery?: boolean }
  ): void {
    const preAccessFilters = this.preAccessFiltersApplied();
    if (preAccessFilters.length > 0) {
      const mergedSnapshot = this.preAccessMergeService.applyToSnapshot(
        snapshot,
        preAccessFilters,
        priority
      );
      this.gridStateApplier.applySnapshot(this.grid, mergedSnapshot, options);
    } else {
      this.gridStateApplier.applySnapshot(this.grid, snapshot, options);
    }
  }

  private applyColumnFilters(filters: ColumnFilter[]): void {
    this.gridStateApplier.applyFilters(this.grid, filters);
  }


  /**
   * Set pre-access filters and apply them to the current layout.
   * Pre-access filters have priority over layout filters when initially applied.
   * Only filters with enforceOnQueries: true remain forced on subsequent queries.
   */
  setPreAccessFilters(filters: PreAccessFilterValue[]): void {
    this.preAccessFiltersApplied.set(filters);
    this.syncPreAccessFiltersToBackend(filters);

    const currentLayout = this.selectedLayout();
    if (currentLayout?.snapshot) {
      this.applySnapshot(currentLayout.snapshot, 'preaccess');
    } else {
      const preAccessAsColumnFilters = this.preAccessMergeService.toColumnFilters(filters);
      this.gridStateApplier.applyFilters(this.grid, preAccessAsColumnFilters);
    }
  }

  /**
   * Clear pre-access filters
   */
  clearPreAccessFilters(): void {
    this.preAccessFiltersApplied.set([]);
    this.syncPreAccessFiltersToBackend([]);

    const currentLayout = this.selectedLayout();
    if (currentLayout?.snapshot) {
      this.applySnapshot(currentLayout.snapshot);
    }
  }

  /**
   * Check if there are active pre-access filters
   */
  hasPreAccessFilters(): boolean {
    return this.preAccessMergeService.hasActiveFilters(this.preAccessFiltersApplied());
  }

  /**
   * Enforced pre-access filters constrain the data set for the lifetime of the table.
   * Keep their query values outside the mutable column-filter state so opening
   * or clearing a column filter cannot remove them from subsequent requests.
   */
  private syncPreAccessFiltersToBackend(filters: PreAccessFilterValue[]): void {
    const backendService = this.grid?.backendService as {
      setForcedFilters?: (filters: Record<string, unknown>) => void;
    } | undefined;
    if (!backendService?.setForcedFilters) {
      return;
    }

    const forcedFilters = this.preAccessMergeService
      .toColumnFilters(filters.filter(filter => this.enforcedPrefilterColumnIds.has(filter.columnId)))
      .reduce<Record<string, unknown>>((result, filter) => {
        const queryKey = filter.queryKey || filter.columnId;
        const searchTerms = filter.searchTerms || [];

        if (filter.operator === 'RangeInclusive' && searchTerms.length === 2) {
          const [from, to] = searchTerms;
          result[queryKey] = {
            ...(from !== null && from !== undefined ? { from } : {}),
            ...(to !== null && to !== undefined ? { to } : {}),
          };
        } else {
          result[queryKey] = searchTerms.length === 1 ? searchTerms[0] : searchTerms;
        }

        return result;
      }, {});

    backendService.setForcedFilters(forcedFilters);
  }
}
